import { notNull, notNullish } from "@de-xima/xima-common-js-lang";
import $ from "jquery";

import type { IBeanUploadChecker, IUploadInfo } from "./Header.ts";

/**
 * Appends the class `document-dragging` to the document element when a file or element
 * is dragged over the document.
 *
 * This class can be used to style the document when a file or element is dragged over it.
 */
function appendGeneralDraggingClass(): void {
  // dragleave is also fired when the mouse moves from one element to another. This
  // removes the class too early. Immediately afterwards, a dragover is fired on the
  // next element, which adds the class again. This results in flickering. To prevent
  // this, we add a timeout so that the class is only removed after a certain amount of
  // time.
  const html = document.documentElement;
  let dragleaveTimeoutId: ReturnType<typeof setTimeout> | undefined;
  window.addEventListener("dragover", (e) => {
    e.preventDefault();
    if (dragleaveTimeoutId !== undefined) {
      clearTimeout(dragleaveTimeoutId);
      dragleaveTimeoutId = undefined;
    }
    if (!html.classList.contains("document-dragging")) {
      html.classList.add("document-dragging");
    }
  });
  window.addEventListener("dragleave", () => {
    if (dragleaveTimeoutId !== undefined) {
      clearTimeout(dragleaveTimeoutId);
      dragleaveTimeoutId = undefined;
    }
    dragleaveTimeoutId = setTimeout(() => {
      html.classList.remove("document-dragging");
    }, 250);
  });
  window.addEventListener("drop", () => {
    if (dragleaveTimeoutId !== undefined) {
      clearTimeout(dragleaveTimeoutId);
      dragleaveTimeoutId = undefined;
    }
    html.classList.remove("document-dragging");
  });
}

/**
 * Returns the drop options for the given drop areas
 * @param helperPanel Selector for the helper panel(s).
 * @returns Options for initializing drop zone.
 */
function getDropZoneOptions(helperPanel: string): Record<string, (e: JQuery.TriggeredEvent) => void> {
  return {
    dragend: (e) => {
      if (notNull(e.currentTarget)) {
        $(e.currentTarget).removeClass("ui-state-dragover");
      }
      $(helperPanel).removeClass("ui-state-dragover");
    },
    dragenter: (e) => {
      if (notNull(e.currentTarget)) {
        $(e.currentTarget).addClass("ui-state-dragover");
      }
      $(helperPanel).addClass("ui-state-dragover");
    },
    dragleave: (e) => {
      if (notNull(e.currentTarget)) {
        $(e.currentTarget).removeClass("ui-state-dragover");
      }
      $(helperPanel).removeClass("ui-state-dragover");
    },
    drop: (e) => {
      if (notNull(e.currentTarget)) {
        $(e.currentTarget).removeClass("ui-state-dragover");
      }
      $(helperPanel).removeClass("ui-state-dragover");
    },
  };
}

/**
 * Attaches listeners for dragenter and `dragleave` / `drop`. Forwards these
 * events to `draghoverstart` and `draghoverend`. Makes sure that consecutive
 * dragenter are ignored when not interspersed with `dragleave` events, and vice
 * versa for `dragleave` events.
 *
 * So for example, these events on an element
 * - dragenter
 * - dragenter
 * - dragleave
 * - dragenter
 * - dragleave
 * - drageleave
 *
 * are converted to
 *
 * - draghoverstart
 * - draghoverend
 * - draghoverstart
 * - draghoverend
 * @param elements Elements to process
 */
function attachDragHoverListener(elements: JQuery<HTMLElement | Window>): void {
  for (const element of elements) {
    const $element = $(element);
    const activeElements = new Set<HTMLElement | Window>();

    $element.on("dragenter", (event) => {
      if (activeElements.size === 0) {
        $element.trigger("draghoverstart");
      }
      activeElements.add(event.target);
    });

    $element.on("dragleave drop", (event) => {
      activeElements.delete(event.target);
      if (activeElements.size === 0) {
        $element.trigger("draghoverend");
      }
    });
  }
}

let dragDropInitialized = false;

/**
 * Initialize default drag behavior.
 * @param callback Callback invoked after initialization is done.
 * @returns A function that does the job.
 */
function initDragDrop(callback?: () => void): () => void {
  return () => {
    $(() => {
      if (!dragDropInitialized) {
        const $window = $(window);
        attachDragHoverListener($window);

        $window.on("draghoverstart", () => {
          $(".ui-fileupload").addClass("file-upload-active");
          $(".ui-fileupload-content").addClass("ui-state-highlight");
          $(".file-upload-help-panel").addClass("ui-state-highlight");
        });

        $window.on("draghoverend", () => {
          $(".ui-fileupload").removeClass("file-upload-active");
          $(".ui-fileupload-content").removeClass("ui-state-highlight");
          $(".file-upload-help-panel").removeClass("ui-state-highlight");
        });

        dragDropInitialized = true;
      }
      callback?.();
    });
  };
}

/**
 * Batches processing of file uploads.
 *
 * Creates a callback function for `<p:fileUpload onAdd="...">` that invokes the
 * given function with all selected files, instead of once for each individual
 * file. Normally, the `onAdd` function is called separately for each selected
 * file.
 * @param processFiles Function that processes the added files.
 * @returns The function for the file upload widget.
 */
function createOnAddForAllFiles(
  processFiles: (files: { callback: (file: File) => void; file: File }[]) => Promise<void>,
): (file: File | undefined | null, callback: (processedFile: File) => void) => void {
  let fileData = [] as { callback: (file: File) => void; file: File }[];

  const onAddAll = () => {
    // Ensure this method is run only once, and not for each selected file
    const localFileData = fileData;
    fileData = [];
    if (localFileData.length > 0) {
      processFiles(localFileData).catch((e) => console.error("Could not process files", e));
    }
  };

  /**
   * This function is called once for each selected file. We are interested in
   * running a callback with all selected files. This needs a hack as it's not
   * possible otherwise: onAdd is called synchronously, so we add all files
   * to an array. We then run another function asynchronously, which is guaranteed
   * to run after onAdd was called for each file.
   * @param file File that was added.
   * @param callback Callback we need to invoke.
   */
  const onAdd = (file: File | undefined | null, callback: (processedFile: File) => void) => {
    if (notNullish(file)) {
      fileData.push({ callback: callback, file: file });
      setTimeout(onAddAll);
    }
  };

  return onAdd;
}

/**
 * Reads the currently selected files of the given file upload widget, and sends
 * the meta data of the files to the server (name, size). This method then
 * awaits the response of the server. If there are any conflicts (file with same
 * name), it opens the given conflict resolution dialog. Otherwise, it starts
 * the file upload.
 *
 * This is used to check beforehand which file names exist already and let the
 * user choose whether they want to rename the file or replace the old file etc.
 *
 * @param remoteCommand Remote command that is called with the file names. Is
 * is passed the stringified form of {@link IUploadInfo}.
 * @returns The newly created bean upload checker with the callback functions.
 */
export function createBeanUploadChecker(
  remoteCommand: ((uploadInfoJson: string) => void) | undefined,
): IBeanUploadChecker {
  const fileDataStore = new Map<number, { callback: (file: File) => void; file: File }[]>();
  let keyGen = 0;

  const proceed: IBeanUploadChecker["proceed"] = (callbackKey, ...filesToSkip) => {
    const fileData = fileDataStore.get(callbackKey);
    fileDataStore.delete(callbackKey);
    if (fileData !== undefined) {
      for (const data of fileData) {
        if (filesToSkip.indexOf(data.file.name) < 0) {
          data.callback(data.file);
        }
      }
    }
  };

  const cancel: IBeanUploadChecker["cancel"] = (callbackKey: number) => {
    const fileData = fileDataStore.get(callbackKey);
    fileDataStore.delete(callbackKey);
    if (fileData !== undefined) {
      // nothing to do
    }
  };

  const processFiles = async (fileData: { callback: (file: File) => void; file: File }[]) => {
    const files = fileData.map((data) => ({ name: data.file.name, size: data.file.size }));
    const callbackKey = keyGen;
    keyGen += 1;
    fileDataStore.set(callbackKey, fileData);
    const uploadInfo: IUploadInfo = { callbackKey, files };
    remoteCommand?.(JSON.stringify(uploadInfo));
  };

  const onAdd: IBeanUploadChecker["onAdd"] = createOnAddForAllFiles(processFiles);

  return { cancel, onAdd, proceed };
}

/**
 * Initializes drag & drop for project resources
 */
export const initProjectResourceDragAndDrop: () => void = initDragDrop(() => {
  $("#projectResourceDialog\\:hiddenUploadForm\\:newFileUploadDragDrop .ui-fileupload-content").on(
    getDropZoneOptions(".new-file-help-panel"),
  );
  $("#projectResourceDialog\\:actionbarForm\\:changeFileUploadHidden .ui-fileupload-content").on(
    getDropZoneOptions(".change-file-help-panel"),
  );
});

/**
 * Initializes drag & drop for client resources
 */
export const initClientResourceDragAndDrop: () => void = initDragDrop(() => {
  $("#hiddenUploadForm\\:newFileUploadDragDrop .ui-fileupload-content").on(getDropZoneOptions(".new-file-help-panel"));
  $(".change-resource-upload .ui-fileupload-content").on(getDropZoneOptions(".change-file-help-panel"));
});

/**
 * Initializes drag & drop for data sources
 */
export const initDataSourceDragAndDrop: () => void = initDragDrop(() => {
  $("#hiddenUploadForm\\:newFileUploadDragDrop .ui-fileupload-content").on(getDropZoneOptions(".new-file-help-panel"));
  $(".change-resource-upload .ui-fileupload-content").on(getDropZoneOptions(".change-file-help-panel"));
});

/**
 * Initializes drag & drop for form imports
 */
export const initFormImportDragAndDrop: () => void = initDragDrop(() => {
  $(".card-import-helper .ui-fileupload-content").on(getDropZoneOptions(".form-upload"));
});

/**
 * Initializes drag & drop for database queries
 */
export const initDbQueryDragAndDrop: () => void = initDragDrop(() => {
  $(".change-resource-upload .ui-fileupload-content").on(getDropZoneOptions(".change-file-help-panel"));
});

/**
 * Initializes drag & drop for client plugins
 */
export const initClientPluginsDragAndDrop: () => void = initDragDrop(() => {
  $("#hiddenUploadForm\\:newPluginUploadDragDrop .ui-fileupload-content").on(
    getDropZoneOptions(".new-file-help-panel"),
  );
  $(".change-resource-upload .ui-fileupload-content").on(getDropZoneOptions(".change-file-help-panel"));
});

/**
 * Initializes drag & drop for system plugins
 */
export const initSystemPluginsDragAndDrop: () => void = initDragDrop(() => {
  $("#hiddenUploadForm\\:newPluginUploadDragDrop .ui-fileupload-content").on(
    getDropZoneOptions(".new-file-help-panel"),
  );
  $(".change-resource-upload .ui-fileupload-content").on(getDropZoneOptions(".change-file-help-panel"));
});

export function initBasicDragDrop(): void {
  appendGeneralDraggingClass();
}
